import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { useApi } from '@/hooks/useApi';
import { Loader2 } from 'lucide-react';
import { CreateRouteDto } from '@/types/route.types';

const formSchema = z.object({
  code: z.string().min(1, 'كود المسار مطلوب'),
  name: z.string().min(1, 'اسم المسار مطلوب'),
  from_zone_id: z.string().min(1, 'منطقة الانطلاق مطلوبة'),
  to_zone_id: z.string().min(1, 'منطقة الوصول مطلوبة'),
  estimated_distance_km: z.string().optional(),
  estimated_time_minutes: z.string().optional(),
  is_active: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

interface AddRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface DeliveryZone {
  id: string;
  code: string;
  name: string;
}

export function AddRouteModal({ isOpen, onClose, onSuccess }: AddRouteModalProps) {
  const { toast } = useToast();
  const { post, get, loading } = useApi();
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [loadingZones, setLoadingZones] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: '',
      name: '',
      from_zone_id: '',
      to_zone_id: '',
      estimated_distance_km: '',
      estimated_time_minutes: '',
      is_active: true,
    },
  });

  // Fetch zones on mount
  useEffect(() => {
    const fetchZones = async () => {
      setLoadingZones(true);
      try {
        const response: any = await get('/api/v1/settings/delivery-zones');
        const data = Array.isArray(response) ? response : response?.data || response?.list || [];
        setZones(data);
      } catch (error) {
        console.error('Error fetching zones:', error);
      } finally {
        setLoadingZones(false);
      }
    };

    if (isOpen) {
      fetchZones();
    }
  }, [isOpen]);

  const onSubmit = async (values: FormValues) => {
    try {
      const payload: CreateRouteDto = {
        code: values.code,
        name: values.name,
        from_zone_id: values.from_zone_id,
        to_zone_id: values.to_zone_id,
        is_active: values.is_active,
      };

      if (values.estimated_distance_km) {
        payload.estimated_distance_km = parseFloat(values.estimated_distance_km);
      }
      if (values.estimated_time_minutes) {
        payload.estimated_time_minutes = parseInt(values.estimated_time_minutes);
      }

      await post('/api/v1/settings/routes', payload);
      toast({
        title: 'تم إنشاء المسار بنجاح',
      });
      form.reset();
      onSuccess();
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'حدث خطأ أثناء إنشاء المسار';
      toast({
        title: 'خطأ',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const zoneOptions = zones.map(z => ({ value: z.id, label: `${z.code} - ${z.name}` }));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة مسار جديد</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>كود المسار</FormLabel>
                    <FormControl>
                      <Input placeholder="R-001" {...field} className="font-mono" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>اسم المسار</FormLabel>
                    <FormControl>
                      <Input placeholder="اسم المسار" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="from_zone_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>منطقة الانطلاق</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        options={zoneOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={loadingZones ? 'جاري التحميل...' : 'اختر المنطقة'}
                        disabled={loadingZones}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="to_zone_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>منطقة الوصول</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        options={zoneOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={loadingZones ? 'جاري التحميل...' : 'اختر المنطقة'}
                        disabled={loadingZones}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="estimated_distance_km"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>المسافة التقريبية (كم)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" min="0" placeholder="25.5" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="estimated_time_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>الوقت التقريبي (دقيقة)</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" placeholder="45" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <FormLabel className="text-base cursor-pointer">تفعيل المسار</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex gap-3 pt-4">
              <Button type="submit" disabled={loading} className="flex-1">
                {loading && <Loader2 className="ms-2 h-4 w-4 animate-spin" />}
                إضافة المسار
              </Button>
              <Button type="button" variant="outline" onClick={handleClose}>
                إلغاء
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}